import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AGUFileDropComponent, AGUFileDropRejection } from 'agu_app_shell';
import { CONSENT_REQUIRED_COPY, NotProcessedMessage, READING_STEPS } from './career-explorer.state';

/** The pipeline's registered CV types (`DOCUMENT_CONTENT_TYPES` in research-api.service.ts), as MIME types for `agu-file-drop`. */
export const CV_ACCEPT: readonly string[] = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
];

/** Same cap as the other upload surfaces (P6:1584 sub-copy, brief). */
export const CV_MAX_BYTES = 10 * 1024 * 1024;

/** What the start view shows in place of the drop. */
export type CxStartMode = 'upload' | 'reading' | 'not-processed' | 'failed';

/**
 * The Career Explorer start view (P6:1578-1591, 308-337): the h1/sub, then one of the upload drop with its consent checkbox, the
 * reading steps, the saved-not-processed panel or the "We could not read your CV" error panel. Presentational: the page owns
 * consent, upload and polling and passes them in.
 */
@Component({
    standalone: false,
    selector: 'ra-cx-start',
    templateUrl: './cx-start.component.html',
    styleUrl: './cx-start.component.scss',
})
export class CxStartComponent implements AfterViewInit, OnChanges, OnDestroy {
    private openTimer: ReturnType<typeof setTimeout> | null = null;
    @ViewChild(AGUFileDropComponent) private drop?: AGUFileDropComponent;
    @Input() Mode: CxStartMode = 'upload';
    @Input() Consent = false;
    @Input() PrivacyUrl: string | null = null;
    @Input() Uploading = false;
    /** "Reading cv.pdf" / "Re-reading your CV". */
    @Input() Heading = '';
    /** 0..2: the active reading step; earlier steps show done. */
    @Input() Step: 0 | 1 | 2 | null = null;
    @Input() FileName = '';
    @Input() NotProcessed: NotProcessedMessage | null = null;
    @Input() UploadError: string | null = null;
    /** Replace: open the file picker as soon as the drop is on screen (P6:1827 `fileIn.click()`). */
    @Input() AutoOpen = false;
    /** Replace with a finished CV on file: offers the way back to it. */
    @Input() CanKeep = false;

    @Output() readonly ConsentChange = new EventEmitter<boolean>();
    @Output() readonly FileChosen = new EventEmitter<File>();
    /** A client-side refusal: the copy to show. */
    @Output() readonly Refused = new EventEmitter<string>();
    @Output() readonly ChooseAnother = new EventEmitter<void>();
    @Output() readonly UseProfile = new EventEmitter<void>();
    @Output() readonly Keep = new EventEmitter<void>();

    readonly Steps = READING_STEPS;
    readonly Accept = CV_ACCEPT;
    readonly MaxBytes = CV_MAX_BYTES;
    readonly ConsentCopy = CONSENT_REQUIRED_COPY;

    ngAfterViewInit(): void {
        this.OpenPickerIfAsked();
    }

    /** Both entry paths: the start view created already in replace mode, and an existing one switched into it (e.g. from the not-processed panel). */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['AutoOpen'] || changes['Mode']) this.OpenPickerIfAsked();
    }

    ngOnDestroy(): void {
        this.ClearOpenTimer();
    }

    /**
     * Opens the picker once the drop is on screen, still inside the click's user-activation window. When the drop is created by the
     * same change that asked (not-processed to upload), its `ViewChild` is not there yet, so the attempt is retried a few ticks.
     */
    private OpenPickerIfAsked(attemptsLeft = 5): void {
        this.ClearOpenTimer();
        if (!this.AutoOpen || this.Mode !== 'upload') return;
        this.openTimer = setTimeout(() => {
            this.openTimer = null;
            if (this.drop) this.drop.Open();
            else if (attemptsLeft > 0) this.OpenPickerIfAsked(attemptsLeft - 1);
        }, 0);
    }

    private ClearOpenTimer(): void {
        if (this.openTimer !== null) clearTimeout(this.openTimer);
        this.openTimer = null;
    }

    /** Both handlers cancel the default so a file dropped outside the drop's own area never makes the browser navigate to it. */
    OnDragOver(event: DragEvent): void {
        event.preventDefault();
    }

    OnStrayDrop(event: DragEvent): void {
        event.preventDefault();
    }

    StepClass(i: number): 'done' | 'on' | '' {
        if (this.Step === null) return '';
        return i < this.Step ? 'done' : i === this.Step ? 'on' : '';
    }

    OnConsent(event: Event): void {
        this.ConsentChange.emit((event.target as HTMLInputElement).checked);
    }

    OnRejected(r: AGUFileDropRejection): void {
        this.Refused.emit(r.Reason === 'size' ? 'Files must be under 10 MB.' : 'Only PDF, DOCX or TXT files can be attached.');
    }
}
